import cx from "classnames";
import { ChangeEvent, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import type { MediaItem } from "@/content-hub/page/domain";
import { getMediaUploadForTransaction } from "@/content-hub/store/selectors";
import { getAssetUrl } from "@/content-hub/utils";
import { useAppSelector } from "@/redux/store";

type ImageUploadProps = {
  uploadTransactionId: string;
  imageMetadata: MediaItem;
  onUpload: (imageMetadata: MediaItem) => void;
  defaultImage?: string;
  addBackgroundColor?: boolean;
  defaultImageComponent?: React.ReactNode;
};

export default function ImageUpload({
  uploadTransactionId,
  imageMetadata,
  onUpload,
  defaultImage,
  addBackgroundColor,
  defaultImageComponent,
}: ImageUploadProps) {
  const fileInput = useRef(null);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const mediaUpload = useAppSelector(
    getMediaUploadForTransaction(uploadTransactionId)
  );
  const isUploading = mediaUpload?.status === "processing";

  const handleFileChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      dispatch({
        type: "[page editor] image upload started",
        payload: { uploadTransactionId, file, onSuccess: onUpload },
      });
    },
    [dispatch, onUpload, uploadTransactionId]
  );
  const showDefaultImageComponent =
    !mediaUpload?.temporaryUrl && !imageMetadata?.path && defaultImageComponent;
  return (
    <div className="flex h-auto flex-col gap-y-2">
      {showDefaultImageComponent ? (
        defaultImageComponent
      ) : (
        <img
          src={
            mediaUpload?.temporaryUrl ??
            getAssetUrl(imageMetadata?.path, defaultImage)
          }
          alt=""
          className={cx(
            `rounded-md border border-gray-200 object-contain object-left max-h-40 max-w-full w-fit`,
            {
              "bg-gray-500": addBackgroundColor,
            }
          )}
        />
      )}
      <button
        className="flex w-28 flex-none items-center justify-center rounded border border-gray-500 bg-transparent px-3 py-1 text-sm font-medium leading-5 text-primary"
        onClick={() => fileInput.current?.click()}
      >
        <input
          type="file"
          ref={fileInput}
          accept="image/png,image/jpg,image/jpeg,image/gif,image/webp"
          onChange={handleFileChange}
          className="hidden"
        />
        {isUploading ? t("content-hub.uploading") : t("content-hub.change")}
      </button>
    </div>
  );
}
